import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowViewLink } from '@/components/common/RowActions';
import { HighlightText } from '@/components/common/HighlightText';
import { formatDateRangeWithYear, formatDisplayDate, formatTime } from '@/utils/date';
import type { LeaveApplicationListItem } from '../types/leaveApplication.types';
import { AppliedDaysBadge } from './AppliedDaysBadge';
import { LeaveApplicationStatusBadge } from './LeaveApplicationStatusBadge';
import { LeaveRequestTypeBadge } from './LeaveRequestTypeBadge';
import { LeaveTypeBadge } from './LeaveTypeBadge';

interface GetLeaveApplicationColumnsArgs {
  searchText: string | undefined;
  onOpenApplication: (application: LeaveApplicationListItem) => void;
}

export function getLeaveApplicationColumns({
  searchText,
  onOpenApplication,
}: GetLeaveApplicationColumnsArgs): ColumnDef<LeaveApplicationListItem>[] {
  return [
    createSerialColumn<LeaveApplicationListItem>(),
    {
      accessorKey: 'userName',
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{ name: row.original.userName, hrEmpId: row.original.hrEmpId, mobile: null }}
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ row }) =>
        row.original.amName ? (
          <span className="font-medium">
            <HighlightText text={row.original.amName} highlightedText={searchText} />
          </span>
        ) : (
          <EmptyValue />
        ),
    },
    getAreaColumn<LeaveApplicationListItem>(
      (application) => ({
        divisionName: application.division,
        districtName: application.district,
        thanaName: application.thana,
      }),
      { searchText, isCommaSeparated: true },
    ),
    {
      accessorKey: 'leaveApplyDate',
      header: 'Apply Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.leaveApplyDate)}
          secondary={formatTime(row.original.leaveApplyDate)}
        />
      ),
    },
    {
      accessorKey: 'requestType',
      header: 'Request Type',
      cell: ({ row }) => <LeaveRequestTypeBadge requestType={row.original.requestType} />,
    },
    {
      accessorKey: 'leaveTypeName',
      header: 'Leave Type',
      cell: ({ row }) =>
        row.original.leaveTypeName ? (
          <LeaveTypeBadge leaveTypeName={row.original.leaveTypeName} />
        ) : (
          <EmptyValue />
        ),
    },
    {
      id: 'appliedDates',
      header: 'Applied Dates',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDateRangeWithYear(row.original.dateFrom, row.original.dateTo)}
          secondary={
            row.original.appliedDays === null ? null : (
              <AppliedDaysBadge appliedDays={row.original.appliedDays} />
            )
          }
        />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <LeaveApplicationStatusBadge status={row.original.status} />,
    },
    createActionsColumn<LeaveApplicationListItem>((application) => (
      <RowViewLink
        to="/hr-form/leave-applications/$applicationId"
        params={{ applicationId: application.id }}
        target="_blank"
        rel="noreferrer"
        onClick={() => onOpenApplication(application)}
        aria-label={`View leave application of ${application.userName}, opens in a new tab`}
      />
    )),
  ];
}
